import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, XAxis, YAxis, LineChart, Line, Tooltip, Legend } from "recharts";
import { getRandomColor } from "../../../Core";
import styles from '../Styles/AnalyzeProducts.module.css';
import { ProductOnDate, ProductOnDateRange, getPlaces } from "../../../apiMethod/analyze";
import { getCategories, getProducts } from "../../../apiMethod/products";
import { Alert, Button, ButtonGroup, ToggleButton, Modal, Form, Row, Col } from "react-bootstrap";
import { FaCircle, FaChevronLeft, FaChevronRight, FaCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from 'date-fns/locale/ru';

registerLocale('ru', ru);

const dayOfWeek = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

function AnalyzeProducts({ jwt }) {
    const updateColors = (array) => {
        array.map(o => {
            o.color = getRandomColor();
            return o;
        });

        return array;
    }

    const [products, setProducts] = useState(null);
    const [productsSend, setProductsSend] = useState(null);
    const [currentDateIndex, setCurrentDateIndex] = useState(0);
    const [categories, setCategories] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
    const [ranges] = useState([
        { name: 'По дням', value: 'days' },
        { name: 'По неделям', value: 'weeks' },
        { name: 'По месяцам', value: 'months' },
        { name: 'По годам', value: 'years' }
    ]);
    const [radioValue, setRadioValue] = useState('days');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [sumAll, setSumAll] = useState(-10);
    const [showDateRangeModal, setShowDateRangeModal] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [isCustomRange, setIsCustomRange] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);
    const [isLoadingComparison, setIsLoadingComparison] = useState(false);
    const [places, setPlaces] = useState([]);
    const [selectedPlaces, setSelectedPlaces] = useState([]);

    useEffect(() => {
        async function didMount() {
            await updateTimeSpanValue(radioValue, null);

            const responseCategories = await getCategories(jwt);
            const products = await getProducts(jwt);
            const placesResponse = await getPlaces(jwt);

            if (!products || !responseCategories || !placesResponse)
                return;

            setCategories(responseCategories);
            setProducts(updateColors(products));
            setPlaces(placesResponse);
        }

        didMount();
    }, []);

    useEffect(() => {
        if (products && categories && places) {
            updateTimeSpanValue(radioValue, isCustomRange ? null : currentDate);
        }
    }, [selectedPlaces]);

    window.addEventListener('resize', async function (event) {
        setIsMobile(window.innerWidth <= 640);
    }, true);

    const handlePlaceClick = (placeId) => {
        setSelectedPlaces(prev => {
            if (prev.includes(placeId)) {
                return prev.filter(id => id !== placeId);
            } else {
                return [...prev, placeId];
            }
        });
    };

    const fetchComparisonData = async (value, date) => {
        if (isCustomRange) {
            const prevStartDate = new Date(startDate);
            const prevEndDate = new Date(endDate);
            const diffTime = endDate.getTime() - startDate.getTime();

            prevStartDate.setTime(prevStartDate.getTime() - diffTime);
            prevEndDate.setTime(prevEndDate.getTime() - diffTime);

            return await ProductOnDateRange(value, prevStartDate.toJSON().split('T')[0],
                prevEndDate.toJSON().split('T')[0], jwt, selectedPlaces);
        } else {
            let previousDate = new Date(date || currentDate);

            if (value === 'days') {
                previousDate.setDate(previousDate.getDate() - 7);
                return await ProductOnDate(value, previousDate.toJSON().split('T')[0], jwt, selectedPlaces);
            } else if (value === 'weeks') {
                previousDate.setDate(previousDate.getDate() - 42);
                return await ProductOnDate(value, previousDate.toJSON().split('T')[0], jwt, selectedPlaces);
            } else if (value === 'months') {
                const previousYear = (date || currentDate).getFullYear() - 1;
                const previousYearDate = new Date(previousYear, (date || currentDate).getMonth(), 1);
                return await ProductOnDate(value, previousYearDate.toJSON().split('T')[0], jwt, selectedPlaces);
            }
            return null;
        }
    };

    const updateTimeSpanValue = async (value, date) => {
        let response;

        if (isCustomRange) {
            response = await ProductOnDateRange(value, startDate.toJSON().split('T')[0],
                endDate.toJSON().split('T')[0], jwt, selectedPlaces);
        } else {
            response = await ProductOnDate(value, date?.toJSON().split('T')[0], jwt, selectedPlaces);
        }

        if (response && Array.isArray(response)) {
            response = response.filter(item => item !== null && item !== undefined);

            // Сначала добавляем displayDate для всех элементов
            response = response.map(item => {
                let displayDate;
                if (value === 'days') {
                    displayDate = dayOfWeek[+item.date] + (item.dateFormat ? ` (${item.dateFormat})` : '');
                } else if (value === 'weeks') {
                    displayDate = `${item.startWeek} - ${item.endWeek}`;
                } else if (value === 'months') {
                    displayDate = months[+item.date - 1] + (item.year ? ` ${item.year}` : '');
                } else if (value === 'years') {
                    displayDate = item.date.toString();
                } else {
                    displayDate = item.date;
                }
                return { ...item, displayDate };
            });

            // Только после этого применяем сортировку для недель
            if (value === 'weeks') {
                response.sort((a, b) => {
                    // Приоритет: weekKey (если есть)
                    if (a.weekKey && b.weekKey) {
                        return a.weekKey.localeCompare(b.weekKey);
                    }
                    // Иначе по году и номеру недели
                    const yearA = a.year || (a.startWeek ? parseInt(a.startWeek.split('.')[2]) : 0);
                    const yearB = b.year || (b.startWeek ? parseInt(b.startWeek.split('.')[2]) : 0);
                    if (yearA !== yearB) return yearA - yearB;
                    return (a.date || 0) - (b.date || 0);
                });
            }

            // Для стандартного режима перезаписываем поле date (если нужно)
            if (!isCustomRange && value !== 'weeks') {
                response = response.map(o => {
                    if (value === 'days') o.date = dayOfWeek[+o.date];
                    else if (value === 'months') o.date = months[+o.date - 1] + (o.year ? ` ${o.year}` : '');
                    return o;
                });
            }
        } else {
            response = [];
        }

        setCurrentDate(date ? date : new Date());

        if (response) {
            if (response.length > 0)
                setSumAll(response.map(o => o.sum).reduce((a, o) => a + o));
            else setSumAll(0);
            setProductsSend(response);
        } else {
            setSumAll(0);
            setProductsSend([]);
        }

        if (!isCustomRange && value !== 'years') {
            setIsLoadingComparison(true);
            try {
                const previousResponse = await fetchComparisonData(value, date);

                if (previousResponse && value !== 'weeks') {
                    const formattedPrevious = previousResponse.map(o => {
                        if (value === 'days')
                            o.date = dayOfWeek[+o.date];
                        else if (value === 'months')
                            o.date = months[+o.date - 1] + (o.year ? ` ${o.year}` : '');
                        return o;
                    });
                    setComparisonData(formattedPrevious);
                } else {
                    setComparisonData(previousResponse);
                }
            } catch (error) {
                console.error('Error loading comparison data:', error);
                setComparisonData(null);
            }
            setIsLoadingComparison(false);
        } else {
            setComparisonData(null);
        }

        setRadioValue(value);
    }

    const getCombinedData = () => {
        if (radioValue === 'years' || !comparisonData || !productsSend) {
            return [];
        }

        const currentData = getFilteredData();

        if (radioValue === 'weeks') {
            return currentData.map((currentItem, index) => {
                const previousItem = comparisonData[index];
                return {
                    name: `Неделя ${currentItem.date}`,
                    current: currentItem.filteredSum,
                    previous: previousItem ? calculateFilteredSum(previousItem) : 0,
                    dateRange: `${currentItem.startWeek} - ${currentItem.endWeek}`
                };
            });
        }

        const combined = currentData.map((currentItem, index) => {
            const previousItem = comparisonData[index];
            return {
                name: currentItem.date,
                current: currentItem.filteredSum,
                previous: previousItem ? calculateFilteredSum(previousItem) : 0
            };
        });

        return combined;
    };

    const getDateTimeMove = (date, isLeft) => {
        if (isCustomRange) return date;

        if (radioValue === 'years')
            return new Date(new Date(date).setFullYear(date.getFullYear() - (isLeft ? 5 : -5)));

        if (radioValue === 'months')
            return new Date(new Date(date).setFullYear(date.getFullYear() - (isLeft ? 1 : -1)));

        if (radioValue === 'weeks')
            return new Date(new Date(date).setDate(date.getDate() - (isLeft ? 42 : -42)));

        if (radioValue === 'days')
            return new Date(new Date(date).setDate(date.getDate() - (isLeft ? 7 : -7)));

        return new Date();
    }

    const handleDateRangeSubmit = async () => {
        setIsCustomRange(true);
        await updateTimeSpanValue(radioValue, null);
        setShowDateRangeModal(false);
    }

    const handleResetDateRange = async () => {
        setIsCustomRange(false);
        await updateTimeSpanValue(radioValue, new Date());
    }

    const handleCategoryClick = (categoryId) => {
        const categoryProducts = products.filter(product => product.categoryId === categoryId);
        const categoryProductIds = categoryProducts.map(p => p.id);

        const allSelected = categoryProductIds.every(id => selectedProducts.includes(id));

        if (allSelected) {
            setSelectedProducts(prev => prev.filter(id => !categoryProductIds.includes(id)));
        } else {
            setSelectedProducts(prev => {
                const newSelection = [...prev];
                categoryProductIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        }
    };

    const handleProductClick = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const getFilteredProducts = () => {
        if (selectedProducts.length === 0) {
            return products;
        }
        return products.filter(product => selectedProducts.includes(product.id));
    };

    const calculateFilteredSum = (data) => {
        if (!data || !data.products) return 0;

        const filteredProducts = getFilteredProducts();
        const filteredProductIds = filteredProducts.map(p => p.id);

        return data.products
            .filter(product => filteredProductIds.includes(product.productId))
            .reduce((sum, product) => sum + product.value, 0);
    };

    const getFilteredData = () => {
        if (!productsSend) return [];

        return productsSend.map(data => ({
            ...data,
            filteredSum: calculateFilteredSum(data)
        }));
    };

    const getFilteredBars = () => {
        const filteredProducts = getFilteredProducts();
        return filteredProducts.map((item, index) => (
            <Bar
                onClick={(data, dateIndex) => setCurrentDateIndex(dateIndex)}
                key={item.id}
                dataKey={(data, index) => {
                    const result = data?.products.find(o => o.productId == item.id);
                    return result ? result.value : 0;
                }}
                stackId={1}
                name={item.name}
                fill={item.color}
            >
                {index === filteredProducts.length - 1 ? (
                    <LabelList position="top" fontSize={14} dataKey={'filteredSum'} />
                ) : null}
            </Bar>
        ));
    };

    const getFilteredTotalSum = () => {
        if (!productsSend) return 0;

        const filteredData = getFilteredData();
        return filteredData.reduce((total, data) => total + data.filteredSum, 0);
    };

    const getActiveFilterName = () => {
        const activeFilters = [];

        if (selectedProducts.length > 0) {
            const selectedCategories = [...new Set(
                selectedProducts.map(productId => {
                    const product = products.find(p => p.id === productId);
                    return product ? product.categoryId : null;
                }).filter(id => id !== null)
            )];

            if (selectedCategories.length === 1) {
                const category = categories.find(c => c.id === selectedCategories[0]);
                const categoryProducts = products.filter(p => p.categoryId === selectedCategories[0]);
                const allCategoryProductsSelected = categoryProducts.every(p =>
                    selectedProducts.includes(p.id)
                );

                if (allCategoryProductsSelected && category) {
                    activeFilters.push(`Категория: ${category.name}`);
                } else {
                    activeFilters.push(`Товары: ${selectedProducts.length}`);
                }
            } else {
                activeFilters.push(`Товары: ${selectedProducts.length}`);
            }
        }

        if (selectedPlaces.length > 0) {
            if (selectedPlaces.length === 1) {
                const place = places.find(p => p.id === selectedPlaces[0]);
                if (place) {
                    activeFilters.push(`Платформа: ${place.name}`);
                }
            } else {
                activeFilters.push(`Платформы: ${selectedPlaces.length}`);
            }
        }

        return activeFilters.length > 0 ? activeFilters.join(' • ') : null;
    };

    const handleResetAllFilters = () => {
        setSelectedProducts([]);
        setSelectedPlaces([]);

    };

    if (!products || !categories)
        return <Alert variant="danger" dismissible>
            <Alert.Heading>Ох, щелк! У вас ошибка!</Alert.Heading>
            <p>
                Что-то пошло не по плану, попробуйте перезагрузить страницу! Если не помогло тогда сообщите разработчику и дождитесь обновления.
            </p>
        </Alert>;

    const ViewProduct = ({ product }) => {
        const currentDate = productsSend[currentDateIndex] ?? [];
        const value = currentDate.products?.find(o => o.productId == product.id);

        const isSelected = selectedProducts.includes(product.id);

        return (
            <button
                onClick={() => handleProductClick(product.id)}
                className={`${styles.element} ${isSelected ? styles.productActive : ''}`}
                key={product.id}
            >
                <FaCircle style={{ width: 10, color: product.color }} />
                <label className={styles.elementText}>
                    {product.name}: <strong>{value ? value.value : 0}</strong>
                    {isSelected && ' ✓'}
                </label>
            </button>
        );
    }

    const Views = ({ products }) => {
        return products.map((product) => <ViewProduct key={product.id} product={product} />);
    }

    const ViewDateMove = ({ date }) => {
        if (!productsSend || productsSend.length <= 0)
            return 'загрузка';

        if (isCustomRange) {
            if (radioValue === 'years' && productsSend.length > 0) {
                const validYears = productsSend
                    .map(item => item.date)
                    .filter(year => typeof year === 'number' && !isNaN(year));
                if (validYears.length > 0) {
                    const minYear = Math.min(...validYears);
                    const maxYear = Math.max(...validYears);
                    return `${minYear} - ${maxYear}`;
                }
                return 'нет данных';
            }

            if (radioValue === 'months' && productsSend.length > 0) {
                const validItems = productsSend.filter(item => {
                    const yearNum = Number(item.year);
                    const monthNum = Number(item.date);
                    return item != null && !isNaN(yearNum) && !isNaN(monthNum) && monthNum >= 1 && monthNum <= 12;
                });
                if (validItems.length === 0) return 'нет данных';

                const timestamps = validItems.map(item => {
                    const yearNum = Number(item.year);
                    const monthNum = Number(item.date) - 1;
                    const date = new Date(Date.UTC(yearNum, monthNum, 1));
                    return date.getTime();
                }).filter(ts => !isNaN(ts));

                if (timestamps.length === 0) return 'нет данных';

                const minTime = Math.min(...timestamps);
                const maxTime = Math.max(...timestamps);
                const minDate = new Date(minTime);
                const maxDate = new Date(maxTime);
                if (isNaN(minDate.getTime()) || isNaN(maxDate.getTime())) return 'нет данных';

                return `${months[minDate.getUTCMonth()] || '?'} ${minDate.getUTCFullYear()} - ${months[maxDate.getUTCMonth()] || '?'} ${maxDate.getUTCFullYear()}`;
            }

            if (radioValue === 'weeks' && productsSend.length > 0) {
                const validItems = productsSend.filter(item =>
                    item.startWeek && item.endWeek
                );
                if (validItems.length === 0) return 'нет данных';

                const startTimestamps = validItems.map(item => {
                    const [day, month, year] = item.startWeek.split('.');
                    return new Date(`${year}-${month}-${day}`).getTime();
                }).filter(ts => !isNaN(ts));

                const endTimestamps = validItems.map(item => {
                    const [day, month, year] = item.endWeek.split('.');
                    return new Date(`${year}-${month}-${day}`).getTime();
                }).filter(ts => !isNaN(ts));

                if (startTimestamps.length === 0 || endTimestamps.length === 0) return 'нет данных';

                const minTime = Math.min(...startTimestamps);
                const maxTime = Math.max(...endTimestamps);
                const minDate = new Date(minTime);
                const maxDate = new Date(maxTime);

                return `${minDate.toLocaleDateString('ru-RU')} - ${maxDate.toLocaleDateString('ru-RU')}`;
            }

            if (radioValue === 'days' && productsSend.length > 0) {
                const validItems = productsSend.filter(item => item.dateFormat);
                if (validItems.length === 0) return 'нет данных';

                const timestamps = validItems.map(item => {
                    const [day, month, year] = item.dateFormat.split('.');
                    return new Date(`${year}-${month}-${day}`).getTime();
                }).filter(ts => !isNaN(ts));

                if (timestamps.length === 0) return 'нет данных';

                const minTime = Math.min(...timestamps);
                const maxTime = Math.max(...timestamps);
                const minDate = new Date(minTime);
                const maxDate = new Date(maxTime);

                return `${minDate.toLocaleDateString('ru-RU')} - ${maxDate.toLocaleDateString('ru-RU')}`;
            }

            // Если ни одно из условий не сработало, показываем исходные даты
            return `${startDate.toLocaleDateString('ru-RU')} - ${endDate.toLocaleDateString('ru-RU')}`;
        }

        if (radioValue === 'years' && productsSend.length > 0) {
            const validYears = productsSend
                .map(item => item.date)
                .filter(year => typeof year === 'number' && !isNaN(year));
            if (validYears.length > 0) {
                const minYear = Math.min(...validYears);
                const maxYear = Math.max(...validYears);
                return `${minYear} - ${maxYear}`;
            }
            return 'нет данных';
        }

        if (radioValue === 'months') {
            return date.toLocaleString('default', { year: 'numeric' });
        }

        if (radioValue === 'weeks' && productsSend.length > 0) {
            const sortedWeeks = [...productsSend].sort((a, b) => a.date - b.date);
            const firstWeek = sortedWeeks[0];
            const lastWeek = sortedWeeks[sortedWeeks.length - 1];
            return `${firstWeek.startWeek} - ${lastWeek.endWeek}`;
        }

        if (radioValue === 'days' && productsSend.length > 0) {
            const sortedDays = [...productsSend].sort((a, b) => {
                const dateFormatA = a.dateFormat || '';
                const dateFormatB = b.dateFormat || '';
                if (!dateFormatA || !dateFormatB) return 0;
                const [dayA, monthA, yearA] = dateFormatA.split('.');
                const [dayB, monthB, yearB] = dateFormatB.split('.');
                return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
            });
            const firstDay = sortedDays[0];
            const lastDay = sortedDays[sortedDays.length - 1];
            if (firstDay && firstDay.dateFormat && lastDay && lastDay.dateFormat) {
                return `${firstDay.dateFormat} - ${lastDay.dateFormat}`;
            }
            return 'нет данных';
        }

        return 'нет данных';
    };

    const filteredData = getFilteredData();
    const filteredTotalSum = getFilteredTotalSum();
    const activeFilterName = getActiveFilterName();
    const combinedData = getCombinedData();
    const showComparisonGraph = !isCustomRange && radioValue !== 'years' && combinedData.length > 0;

    return (
        <div className={styles.main}>
            <div className={styles.content}>
                <div className={styles.headContent}>
                    {getActiveFilterName() && (
                        <Alert variant="info" className="mb-2" style={{ marginBottom: '10px', padding: '8px 12px' }}>
                            Активный фильтр: <strong>{getActiveFilterName()}</strong>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="ms-2"
                                onClick={handleResetAllFilters}
                                style={{ marginLeft: '10px' }}
                            >
                                × Сбросить все фильтры
                            </Button>
                        </Alert>
                    )}

                    <label className={styles.titleChart}>
                        {getActiveFilterName()
                            ? `Проданная продукция - ${getActiveFilterName()}`
                            : 'Проданная продукция'
                        }
                    </label>
                    <div className={styles.dateBox}>
                        <ButtonGroup aria-label="Basic example">
                            {!isCustomRange && (
                                <>
                                    <Button variant="primary" onClick={() => updateTimeSpanValue(radioValue, getDateTimeMove(currentDate, true))}>
                                        <FaChevronLeft style={{ color: 'white' }} />
                                    </Button>
                                    <div className={styles.dateMiddleText}>
                                        <ViewDateMove date={currentDate} />
                                    </div>
                                    <Button variant="primary" onClick={() => updateTimeSpanValue(radioValue, getDateTimeMove(currentDate, false))}>
                                        <FaChevronRight style={{ color: 'white' }} />
                                    </Button>
                                </>
                            )}
                            {isCustomRange && (
                                <div className={styles.dateMiddleText}>
                                    <ViewDateMove date={currentDate} />
                                </div>
                            )}
                        </ButtonGroup>
                    </div>
                    <div className={styles.dateBox}>
                        <ButtonGroup>
                            {ranges.map((radio, idx) => (
                                <ToggleButton
                                    className={styles.radio}
                                    key={idx}
                                    id={`radio-${idx}`}
                                    type="radio"
                                    name="radio"
                                    variant={radioValue === radio.value ? 'primary' : 'outline-primary'}
                                    value={radio.value}
                                    checked={radioValue === radio.value}
                                    onChange={(e) => updateTimeSpanValue(e.currentTarget.value, isCustomRange ? null : new Date())}
                                >
                                    {radio.name}
                                </ToggleButton>
                            ))}
                        </ButtonGroup>
                    </div>
                    <div className={styles.dateBox}>
                        <Button
                            variant={isCustomRange ? "success" : "outline-secondary"}
                            onClick={() => setShowDateRangeModal(true)}
                        >
                            <FaCalendarAlt /> {isCustomRange ? "Выбран диапазон" : "Выбрать диапазон"}
                        </Button>
                        {isCustomRange && (
                            <Button
                                variant="outline-danger"
                                className="ml-2"
                                onClick={handleResetDateRange}
                            >
                                Сбросить
                            </Button>
                        )}
                    </div>
                </div>

                <div style={{ marginBottom: showComparisonGraph ? '20px' : '0' }}>
                    <ResponsiveContainer width={'100%'} height={isMobile ? 400 : 500}>
                        <BarChart data={getFilteredData()} margin={{ top: 20 }}>
                            {isMobile ? null : (
                                <React.Fragment>
                                    <XAxis dataKey={isCustomRange ? "displayDate" : "date"} stroke="#8884d8" fontSize={12} />
                                    <YAxis width={35} fontSize={9} />
                                </React.Fragment>
                            )}
                            <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                            {getFilteredBars()}
                        </BarChart>
                    </ResponsiveContainer>
                </div>


                {showComparisonGraph && (
                    <div>
                        <h4 style={{ textAlign: 'center', marginBottom: '10px' }}>Сравнение с предыдущим периодом</h4>
                        <ResponsiveContainer width={'100%'} height={isMobile ? 400 : 500}>
                            <LineChart data={combinedData} margin={{ top: 20 }}>
                                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                                <XAxis dataKey="name" stroke="#8884d8" fontSize={12} />
                                <YAxis width={35} fontSize={9} />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="current"
                                    stroke="#8884d8"
                                    name="Текущий период"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="previous"
                                    stroke="#82ca9d"
                                    name="Предыдущий период"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    strokeDasharray="5 5"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className={styles.placesFilter}>
                    <h4 className={styles.filterTitle}>Платформы</h4>
                    <div className={styles.placesContainer}>
                        {places.map(place => (
                            <button
                                key={place.id}
                                onClick={() => handlePlaceClick(place.id)}
                                className={`${styles.placeButton} ${selectedPlaces.includes(place.id) ? styles.placeActive : ''}`}
                            >
                                {place.name}
                                {selectedPlaces.includes(place.id) && ' ✓'}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.currentDateText}>
                    {productsSend?.[currentDateIndex]?.date} - {Math.round((filteredData[currentDateIndex]?.filteredSum / filteredTotalSum) * 10000) / 100 || 0}% (от {filteredTotalSum})
                </div>
                <div className={styles.elements}>
                    {categories.map(c => {
                        const categoryProducts = products.filter(o => o.categoryId == c.id);
                        const allCategoryProductsSelected = categoryProducts.every(p =>
                            selectedProducts.includes(p.id)
                        );
                        return (
                            <div className={styles.categoryItem} key={c.id}>
                                <div
                                    className={`${styles.categoryName} ${allCategoryProductsSelected ? styles.categoryNameActive : ''}`}
                                    onClick={() => handleCategoryClick(c.id)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: allCategoryProductsSelected ? '#e3f2fd' : 'transparent',
                                        padding: '5px 10px',
                                        borderRadius: '4px',
                                        transition: 'background-color 0.2s',
                                        border: allCategoryProductsSelected ? '1px solid #2196f3' : '1px solid transparent',
                                        marginBottom: '5px'
                                    }}
                                >
                                    {c.name.toLocaleUpperCase()}
                                    {allCategoryProductsSelected && ' ✓'}
                                </div>
                                <Views products={categoryProducts} />
                            </div>
                        );
                    })}
                </div>
            </div>

            <Modal show={showDateRangeModal} onHide={() => setShowDateRangeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Выберите диапазон дат</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Начальная дата</Form.Label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control"
                                    locale="ru"
                                    dateFormat="dd.MM.yyyy"
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label>Конечная дата</Form.Label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    className="form-control"
                                    locale="ru"
                                    dateFormat="dd.MM.yyyy"
                                />
                            </Form.Group>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDateRangeModal(false)}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleDateRangeSubmit}>
                        Применить
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AnalyzeProducts;